import { useCallback, useState } from 'react';
import { Box, CircularProgress, Flex } from '@tempmee/react';
import { InviteCandidates } from '@tempmee/react-blocks';
import { InviteSuccessPopup } from '~/components/counterJobs/OpenJobScreenRedesign/InviteSuccessPopup';
import { useInviteFavoriteToJobMutation } from '~/domain/Jobs';
import type { IJob } from '~/models';
import { fireInviteSent, fireShiftDetailsInviteClicked } from '~/posthogs/events';
import { toInviteSkills } from '../utils/inviteCandidateAdapters';
import { useInvitePools } from '../hooks/useInvitePools';

interface IInvitePanelProps {
  job: IJob;
}

/**
 * The open shift's primary column: the `InviteCandidates` block wired to the
 * office's professional pools, the invite mutation and its confirmation popup.
 *
 * The block draws no surface of its own — per the Figma the organism is
 * transparent and the page supplies the card — so the panel shell lives here:
 * `bg-background` with 16px padding, on the shell's subdued page.
 */
export const InvitePanel = ({ job }: IInvitePanelProps) => {
  const officeId = job.offer_owner?.id;
  const {
    isLoading,
    isInviting,
    tabs,
    activeTab,
    onTabChange,
    candidates,
    selectedIds,
    onSelectionChange,
    findRow,
    clearSelection,
  } = useInvitePools({ jobId: job.id, officeId, professionType: job.profession });

  const { mutate: inviteFavoriteToJob, isPending } = useInviteFavoriteToJobMutation();

  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [skillsCandidateId, setSkillsCandidateId] = useState<string | null>(null);

  const handleInvite = useCallback(() => {
    if (selectedIds.length === 0) {
      return;
    }

    fireShiftDetailsInviteClicked({ shiftId: job.id });

    inviteFavoriteToJob(
      { jobId: job.id, userIds: selectedIds, officeId },
      {
        onSuccess: () => {
          fireInviteSent({
            source: 'Shift Details',
            shiftId: job.id,
            totalInvitesSent: selectedIds.length,
          });
          // The mutation invalidates the job query, so the freshly invited
          // professionals move to the Invited tab on the refetch — nothing here
          // has to remount the list.
          setIsSuccessOpen(true);
          clearSelection();
        },
      },
    );
  }, [clearSelection, inviteFavoriteToJob, job.id, officeId, selectedIds]);

  const skillsRow = skillsCandidateId ? findRow(skillsCandidateId) : undefined;
  const closeSkills = useCallback(() => setSkillsCandidateId(null), []);

  return (
    <Box
      p={4}
      className="bg-background rounded-lg"
      data-cy="shift-details-invite-panel"
      data-testid="shift-details-invite-panel"
    >
      {isLoading ? (
        <Flex justify="center" py={10}>
          <CircularProgress />
        </Flex>
      ) : (
        <InviteCandidates
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={onTabChange}
          candidates={candidates}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          onInvite={handleInvite}
          isInviteDisabled={selectedIds.length === 0 || isPending || isInviting}
          onCandidateClick={setSkillsCandidateId}
          skills={skillsRow ? toInviteSkills(skillsRow, closeSkills) : undefined}
          testID="shift-details-invite-candidates"
          inviteTestID="shift-details-invite-button"
          rowTestID="shift-details-invite-row"
          skillsTestID="shift-details-invite-skills"
        />
      )}

      {isSuccessOpen && (
        <InviteSuccessPopup
          dataCy="shift-details-invite-success"
          description="This shift request has been successfully sent to your selected professionals."
          onClose={() => setIsSuccessOpen(false)}
        />
      )}
    </Box>
  );
};
